/**
 * The face, drawn at a size a thumb can hit.
 *
 * A laser catalogue names an upper lip, a chin, sideburns. On the body figure
 * those are unhittable: the drawing renders at about 34 pixels per unit, which
 * makes an upper lip 9 x 3 px — too small to tap and too small to draw. Four of
 * twenty areas therefore had no artwork at all and fell back to being names in
 * a list.
 *
 * So the face is not sub-regions on a 45-pixel head; it is its own figure,
 * beside front and back, in its own coordinate space. The same upper lip is
 * about 40 x 14 px here.
 *
 * ── WHY THIS IS DRAWN AND NOT LICENSED ───────────────────────────────────────
 * There is no openly-licensed face-region map. The open body maps
 * (body_part_selector, react-native-body-highlighter, react-muscle-highlighter)
 * all treat the head as ONE part. The closest clinical asset, UCSF's MoleMapper
 * body map, draws a whole face as three line segments. Wikimedia's SVG heads are
 * cartoon icons and every aesthetic face chart is proprietary. So this is ours,
 * and it owes nobody attribution.
 *
 * ── WHY IT LOOKS THE WAY IT DOES ─────────────────────────────────────────────
 * Proportions follow the classic canon, because inventing them is what made the
 * first attempts read as an alien: eyes on the vertical midline; hairline, brow,
 * nose base and chin in equal thirds; eye width one fifth of the head; nose one
 * eye wide; mouth one and a half.
 *
 * The soft shadows down the temples, under the cheekbones, beside the nose and
 * under the jaw are what separate a face from a mask. A flat fill with features
 * stamped on it reads as a cut-out however carefully the features are drawn.
 *
 * And there is no drawn line down the bridge of the nose. A nose is shading plus
 * its wings and nostrils; outlining it is the single clearest tell of a drawing
 * made by somebody who was not looking at a face.
 *
 * ── SELECTION IS A TINT, NOT PAINT ───────────────────────────────────────────
 * The body fills a chosen region solid, and at body scale that is right because
 * the regions are large. At face scale the same fill turns an upper lip into an
 * orange smear across the mouth. `.regionFaceOpen` therefore tints and outlines
 * instead, so the lips stay visible through the region that covers them.
 */
import React, { useId } from 'react';

/**
 * The regions a catalogue may name.
 *
 * Eight, of which a laser catalogue uses three today. The rest are drawn because
 * a face has them and the next vertical will ask: the GLABELLA especially, which
 * is its own region rather than part of the forehead — a clinic treating between
 * the brows is not treating the forehead, and the two are sold and recorded
 * separately.
 */
const PARTS = {
  forehead:
    'M78,236 C86,168 130,136 200,136 C270,136 314,168 322,236 C316,250 300,254 282,248 '
    + 'C258,240 228,238 200,238 C172,238 142,240 118,248 C100,254 84,250 78,236 Z',
  glabella:
    'M182,236 C190,231 210,231 218,236 C221,248 220,259 217,265 C210,269 190,269 183,265 '
    + 'C180,259 179,248 182,236 Z',
  sideburn_l:
    'M74,248 C70,270 71,294 78,314 C81,322 92,322 93,314 C88,292 87,270 90,250 C90,242 75,240 74,248 Z',
  sideburn_r:
    'M326,248 C330,270 329,294 322,314 C319,322 308,322 307,314 C312,292 313,270 310,250 '
    + 'C310,242 325,240 326,248 Z',
  cheek_l:
    'M126,290 C146,290 162,306 162,326 C162,346 146,362 126,362 C106,362 90,346 90,326 '
    + 'C90,306 106,290 126,290 Z',
  cheek_r:
    'M274,290 C294,290 310,306 310,326 C310,346 294,362 274,362 C254,362 238,346 238,326 '
    + 'C238,306 254,290 274,290 Z',
  upper_lip:
    'M174,358 C185,352 195,357 200,363 C205,357 215,352 226,358 C232,364 230,376 221,380 '
    + 'C211,384 189,384 179,380 C170,376 168,364 174,358 Z',
  jaw_l:
    'M86,322 C94,358 106,388 122,412 C142,438 172,456 200,458 L200,446 C178,440 154,420 138,398 '
    + 'C122,374 108,346 102,320 Z',
  jaw_r:
    'M314,322 C306,358 294,388 278,412 C258,438 228,456 200,458 L200,446 C222,440 246,420 262,398 '
    + 'C278,374 292,346 298,320 Z',
  chin:
    'M174,428 C186,422 214,422 226,428 C228,444 214,456 200,458 C186,456 172,444 174,428 Z',
  neck:
    'M172,470 C184,480 216,480 228,470 C232,498 230,522 226,538 C216,546 184,546 174,538 '
    + 'C169,522 168,498 172,470 Z',
};

/**
 * Everything that is drawn and nothing that is chosen.
 *
 * A React component rather than a list of paths, which is the one place this
 * artwork differs from the silhouette: a face needs a gradient for its roundness
 * and a blur for its shadows, and neither survives being flattened into a list
 * of `d` strings. `useId` keeps the two safe on a page that renders more than
 * one map.
 */
export function FaceDecoration() {
  const uid = useId().replace(/[^\w-]/g, '');
  const skin = `faceSkin-${uid}`;
  const nape = `faceNeck-${uid}`;
  const soft = `faceSoft-${uid}`;

  return (
    <>
      <defs>
        <radialGradient id={skin} cx="44%" cy="34%" r="72%">
          <stop offset="0" stopColor="#F6F8FC" />
          <stop offset="0.62" stopColor="#EAEFF5" />
          <stop offset="1" stopColor="#D9E0EA" />
        </radialGradient>
        <linearGradient id={nape} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#D5DCE7" />
          <stop offset="1" stopColor="#E6EBF2" />
        </linearGradient>
        <filter id={soft} x="-25%" y="-25%" width="150%" height="150%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
      </defs>

      {/* Neck and shoulders, behind the head, running off the frame. */}
      <path
        d="M162,436 C164,470 158,492 148,504 C120,520 74,540 40,562 C20,575 6,586 0,600
           L400,600 C394,586 380,575 360,562 C326,540 280,520 252,504 C242,492 236,470 238,436 Z"
        fill={`url(#${nape})`}
        stroke="#C6CFDB"
        strokeWidth="1.6"
      />

      {/* Ears. */}
      <path
        d="M78,250 C58,244 48,270 55,300 C62,330 72,352 90,354 C98,354 100,344 98,334 L90,258 Z"
        fill="#E6EBF2"
        stroke="#C6CFDB"
        strokeWidth="1.6"
      />
      <path d="M76,270 C66,278 66,300 74,316 C78,325 84,330 89,330" fill="none" stroke="#C2CBD8" strokeWidth="1.4" />
      <path
        d="M322,250 C342,244 352,270 345,300 C338,330 328,352 310,354 C302,354 300,344 302,334 L310,258 Z"
        fill="#E6EBF2"
        stroke="#C6CFDB"
        strokeWidth="1.6"
      />
      <path d="M324,270 C334,278 334,300 326,316 C322,325 316,330 311,330" fill="none" stroke="#C2CBD8" strokeWidth="1.4" />

      {/* The head. */}
      <path
        d="M200,70 C142,70 78,108 68,190 C62,232 66,268 74,300
           C82,336 96,368 116,396 C138,428 168,458 200,460
           C232,458 262,428 284,396 C304,368 318,336 326,300
           C334,268 338,232 332,190 C322,108 258,70 200,70 Z"
        fill={`url(#${skin})`}
        stroke="#C0CAD9"
        strokeWidth="1.8"
      />

      {/* Form shadows — see the note at the top. */}
      <g fill="#4A5C78" opacity="0.085" filter={`url(#${soft})`}>
        <path d="M70,196 C64,238 68,272 76,302 C84,338 98,370 118,398 C106,360 96,320 93,280 C91,244 94,214 98,192 Z" />
        <path d="M330,196 C336,238 332,272 324,302 C316,338 302,370 282,398 C294,360 304,320 307,280 C309,244 306,214 302,192 Z" />
        <ellipse cx="150" cy="330" rx="34" ry="26" />
        <ellipse cx="250" cy="330" rx="34" ry="26" />
        <path d="M186,246 C180,290 178,320 176,344 C182,350 190,352 194,348 C192,318 194,280 198,248 Z" />
        <ellipse cx="200" cy="366" rx="26" ry="8" />
        <ellipse cx="200" cy="432" rx="26" ry="9" />
        <path d="M140,416 C162,446 180,458 200,461 C220,458 238,446 260,416 C242,456 224,474 200,476 C176,474 158,456 140,416 Z" />
      </g>
    </>
  );
}

/**
 * The features, drawn OVER the regions.
 *
 * Separate from the decoration underneath because a chosen upper lip must not
 * bury the mouth it covers: the region tint goes between the two.
 */
export function FaceFeatures() {
  return (
    <>
      {/* Brows: tapered shapes. A round-capped stroke reads as a marker line. */}
      <path d="M112,240 C130,222 160,219 178,231 C160,227 132,231 116,247 Z" fill="#9AA5B6" />
      <path d="M288,240 C270,222 240,219 222,231 C240,227 268,231 284,247 Z" fill="#9AA5B6" />

      <g strokeLinecap="round">
        <path d="M118,266 C132,249 158,249 173,266 C158,281 132,281 118,266 Z" fill="#FAFCFE" stroke="#B4BECC" strokeWidth="1.3" />
        <path d="M282,266 C268,249 242,249 227,266 C242,281 268,281 282,266 Z" fill="#FAFCFE" stroke="#B4BECC" strokeWidth="1.3" />
        <circle cx="146" cy="265" r="11.5" fill="#8B97A8" />
        <circle cx="146" cy="265" r="4.6" fill="#2F3844" />
        <circle cx="142" cy="261" r="2.8" fill="#fff" opacity="0.85" />
        <circle cx="254" cy="265" r="11.5" fill="#8B97A8" />
        <circle cx="254" cy="265" r="4.6" fill="#2F3844" />
        <circle cx="250" cy="261" r="2.8" fill="#fff" opacity="0.85" />
        <path d="M118,266 C132,249 158,249 173,266" fill="none" stroke="#7E8A9C" strokeWidth="2.6" />
        <path d="M282,266 C268,249 242,249 227,266" fill="none" stroke="#7E8A9C" strokeWidth="2.6" />
        <path d="M120,258 C134,243 158,243 175,258" fill="none" stroke="#BAC3D0" strokeWidth="1.2" />
        <path d="M280,258 C266,243 242,243 225,258" fill="none" stroke="#BAC3D0" strokeWidth="1.2" />
      </g>

      {/* Nose: wings, tip and nostrils. The bridge is shading, never a line. */}
      <g fill="none" stroke="#A3AEBE" strokeWidth="1.7" strokeLinecap="round">
        <path d="M176,352 C167,345 168,334 177,330" />
        <path d="M224,352 C233,345 232,334 223,330" />
        <path d="M180,355 C190,360 210,360 220,355" />
        <path d="M193,368 L191,382" />
        <path d="M207,368 L209,382" />
      </g>
      <ellipse cx="185" cy="349" rx="6" ry="3.4" fill="#A3AEBE" transform="rotate(-16 185 349)" />
      <ellipse cx="215" cy="349" rx="6" ry="3.4" fill="#A3AEBE" transform="rotate(16 215 349)" />

      {/* Lips, with a cupid's bow. */}
      <path
        d="M158,390 C170,377 183,385 193,384 L200,388 L207,384 C217,385 230,377 242,390
           C228,395 214,397 200,397 C186,397 172,395 158,390 Z"
        fill="#D9C4C0"
        stroke="#BAA29E"
        strokeWidth="1.3"
      />
      <path
        d="M158,390 C174,388 226,388 242,390 C238,411 220,423 200,423 C180,423 162,411 158,390 Z"
        fill="#E3D0CC"
        stroke="#BAA29E"
        strokeWidth="1.3"
      />
      <path d="M158,390 C176,395 224,395 242,390" fill="none" stroke="#A9908C" strokeWidth="1.7" strokeLinecap="round" />
    </>
  );
}

/**
 * A face view, in the shape every other view of this artwork has.
 *
 * `Decoration` and `Features` are the only additions, and both are optional on
 * the type — a silhouette has neither and renders through `bodyPaths` as it
 * always did.
 */
export const HUMAN_FACE = {
  viewBox: '0 0 400 600',
  strokeWidth: 1.8,
  parts: PARTS,
  Decoration: FaceDecoration,
  Features: FaceFeatures,
  /** The face draws itself; its `parts` are regions, not the body. */
  drawsOwnBody: true,
};

export const FACE_PART_NAMES = Object.keys(PARTS);
